/**
 * Progress Service
 * 
 * Handles communication with the backend progress tracking API.
 * All progress data is stored in Redis (real-time) and MongoDB (persistent).
 */

import axios from 'axios';
import { baseApi } from '../API/api';

class ProgressService {
  /**
   * Get all tasks/runs for a specific user
   * @param {string} userEmail - User email address
   * @returns {Promise<Array>} Array of task objects with progress data
   */
  async getUserTasks(userEmail) {
    try {
      if (!userEmail) {
        throw new Error('User email is required');
      }
      
      const url = `${baseApi}/api/progress/user/${encodeURIComponent(userEmail)}`;
      console.log(`📡 Fetching user tasks from: ${url}`);
      
      const response = await axios.get(url);
      
      console.log(`✅ User tasks response:`, response.data);
      return response.data.tasks || [];
    } catch (error) {
      console.error('❌ Error loading user tasks:', error);
      console.error('  URL:', error.config?.url);
      console.error('  Status:', error.response?.status);
      console.error('  Response:', error.response?.data);
      throw error;
    }
  }

  /**
   * Get specific task progress
   * @param {string} taskId - Task/Run ID
   * @param {string} serviceName - Optional service name filter
   * @param {boolean} aggregate - If true, return aggregate progress
   * @returns {Promise<Object>} Task progress object
   */
  async getTaskProgress(taskId, serviceName = null, aggregate = false) {
    try {
      const params = new URLSearchParams();
      if (serviceName) params.append('service_name', serviceName);
      if (aggregate) params.append('aggregate', 'true');
      
      const url = `${baseApi}/api/progress/${taskId}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error loading task progress:', error);
      throw error;
    }
  }

  /**
   * Connect to real-time progress stream via Server-Sent Events
   * @param {string} taskId - Task/Run ID
   * @param {Function} onProgress - Callback function for progress updates
   * @param {Function} onError - Callback function for errors
   * @param {Function} onComplete - Callback function when stream completes
   * @returns {EventSource} EventSource instance
   */
  connectToProgressStream(taskId, onProgress, onError = null, onComplete = null) {
    const eventSource = new EventSource(
      `${baseApi}/api/progress/${taskId}/stream`
    );

    eventSource.onmessage = (event) => {
      try {
        const progress = JSON.parse(event.data);
        
        // Check if completed
        if (progress.completed || (progress.completed_services > 0 && 
            progress.completed_services === progress.total_services)) {
          if (onComplete) onComplete(progress);
          eventSource.close();
        } else {
          onProgress(progress);
        }
      } catch (error) {
        console.error('Error parsing progress data:', error);
        if (onError) onError(error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      if (onError) onError(error);
      // Don't close on error - EventSource will auto-reconnect
    };

    return eventSource;
  }

  /**
   * Get aggregate progress for a task
   * @param {string} taskId - Task/Run ID
   * @returns {Promise<Object>} Aggregate progress object
   */
  async getAggregateProgress(taskId) {
    return this.getTaskProgress(taskId, null, true);
  }
}

// Export singleton instance
const progressService = new ProgressService();
export default progressService;

